import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import moment, { Moment } from 'moment-timezone';
import TextArea from '../Base/TextArea/TextArea';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import Select, { SelectValue } from '../Base/Select/Select';
import PatientMiniProfile from '../PatientMiniProfile/PatientMiniProfile';
import TextField from '../Base/TextField/TextField';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import apiClient, { IResponse } from '../../Services/ApiClient';
import RemoveButton from '../Button/RemoveButton';
import useNotification from '../../Hooks/useNotification.hook';
import { useEffectAsync } from '../../helpers';
import Text from '../Base/Text/Text';
import FormWrapper from '../Form/FormWrapper';
import PatientTimeZoneInfo, {
	PatientTimeZoneInfoValues,
} from '../Patient/PatientTimeZoneInfo';
import {
	Calendar,
	CaseTypeType,
	Meta,
	useLazyHealthPlansQuery,
	useLazyHealthPlanUserQuery,
	useLazyWorkplaceCaseTypesDoctorQuery,
} from '../../graphql/graphql';
import { adjustDstBreakPoint, getMomentDate } from '../../Utils/dates.util';
import useTranslation from '../../Hooks/useTranslation';
import useWorkplaceRole from '../../Modules/Workplace/Hooks/useWorkplaceRole';
import { useAppContext } from '../../Services/AppContext';
import DoctorSelectField from '../Form/components/DoctorSelectField';
import Checkbox from '../Base/Checkbox/Checkbox';
import { useIsLatam } from '../../Modules/Region/Services/region.service';
import PatientSelect from '../../Modules/Patient/Components/PatientSelect/PatientSelect';
import { HealthPlanCode } from '../../Enums/HealthPlanCode.enum';
import styles from './CalendarPatientEvent.module.scss';
import useUserRelations, {
	HealthPlanRelation,
} from '../../Modules/User/Hooks/useUserRelations';
import { checkIsTimeSameOrAfter } from '../../Modules/Events/Services/eventInterval.service';
import Loading from '../Loading';
import PatientManualRegistrationModal from '../../Modules/EArchive/Components/PatientManualRegistration/PatientManualRegistrationModal';
import { PatientManualRegistrationParams } from '../../Modules/EArchive/Components/PatientManualRegistration/PatientManualRegistration';
import { Theme } from '../../Enums/Theme.enum';
import LinkButton from '../Button/LinkButton';
import useCustomSettings from '../../Modules/Base/Hooks/useCustomSettings';
import { CustomSettingKey } from '../../Modules/Base/Enums/CustomSettingKey.enum';

interface ICalendarPatientEvent {
	selectedDate: any;
	patientId?: string;
	caseId?: string;
	onSuccess?: () => void;
	workplaceId?: string;
	order?: any;
	params?: any;
	onEventIsCreated?: (response: IResponse) => void;
	healthPlanCode?: HealthPlanCode;
	calendar?: Calendar;
	sendWorkplaceId?: boolean;
}

interface WorkplaceCaseTypeSelectValue extends SelectValue {
	eventInterval?: number;
}

interface HealthPlanSelectValue extends SelectValue {
	id?: string;
}

interface CalendarPatientEventFormValues {
	caseType: WorkplaceCaseTypeSelectValue;
	date: Moment;
	healthPlan?: HealthPlanSelectValue;
	medicalProfessional: SelectValue;
	meetingType: SelectValue;
	note: string;
	patientId: SelectValue;
	timeFrom: Moment;
	timeTo: Moment;
	specification?: string;
	paidMeeting?: boolean;
}

const CalendarPatientEvent = (props: ICalendarPatientEvent) => {
	const {
		selectedDate,
		patientId,
		caseId,
		onSuccess,
		order,
		params,
		onEventIsCreated,
		healthPlanCode,
		calendar,
		sendWorkplaceId = true,
	} = props;
	const { t } = useTranslation();
	const { userData, globalTemplate, loggedUserId } = useAppContext();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();

	const [workplaceCaseTypes, setWorkplaceCaseTypes] = useState<
		WorkplaceCaseTypeSelectValue[]
	>([]);
	const [loadingWorkplaceCaseTypes, setLoadingWorkplaceCaseTypes] =
		useState(false);
	const [areHealthPlansLoading, setAreHealthPlansLoading] = useState(false);
	const [healthPlanOptions, setHealthPlanOptions] = useState<
		HealthPlanSelectValue[]
	>([]);
	const [loading, setLoading] = useState(!!order);
	const [toggleOther, setToggleOther] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [usersTimeDiff, setUsersTimeDiff] = useState<string | undefined>();
	const [patientTimezone, setPatientTimezone] = useState<string | undefined>();
	const [getHealthPlans] = useLazyHealthPlansQuery();
	const [getHealthPlanUser] = useLazyHealthPlanUserQuery();
	const [getWorkplaceCaseTypesDoctor] = useLazyWorkplaceCaseTypesDoctorQuery();
	const isLatam = useIsLatam(globalTemplate);
	const { getHealthPlanDoctorId, getHealthPlanRelationCodeByHealthPlanCode } =
		useUserRelations();
	const [isCaseTypeSelectVisible, setIsCaseTypeSelectVisible] = useState(true);
	const [isRegisterNewPatientOpen, setIsRegisterNewPatientOpen] =
		useState(false);

	const workplaceId = props.workplaceId ?? useAppContext().workplaceId;

	const { getCustomSettings, saveCustomSettings } =
		useCustomSettings(workplaceId);

	const { id: userId, workplaceId: userWorkplaceId } = userData;

	const { isDoctor, isNurse } = useWorkplaceRole({
		userId,
		workplaceId: userWorkplaceId,
	});

	const isNurseView = !isDoctor && isNurse;

	const [defaultValues, setDefaultValues] = useState<any>({
		date: selectedDate,
		timeFrom: selectedDate,
	});

	const [selectedPatientId, setSelectedPatientId] = useState(patientId);
	const [timeFrom, setTimeFrom] = useState(null);
	const [timeTo, setTimeTo] = useState(null);

	useEffectAsync(async () => {
		if (isNurse && patientId && healthPlanCode) {
			const healthPlanRelationCode: HealthPlanRelation | null =
				getHealthPlanRelationCodeByHealthPlanCode(healthPlanCode);
			if (healthPlanRelationCode) {
				const healthPlanDoctorId = await getHealthPlanDoctorId(
					patientId,
					workplaceId,
					healthPlanRelationCode,
				);
				formMethods.setValue('medicalProfessional', {
					value: healthPlanDoctorId,
				});
			}
		}
	});

	useEffectAsync(async () => {
		const data = await getCustomSettings(
			CustomSettingKey.CALENDAR_EVENT_CREATE_DEFAULT_DATA,
		);
		if (!order && data) {
			setDefaultValues(prev => ({ ...prev, ...data }));
		}
	}, []);

	useEffect(() => {
		formMethods.reset(defaultValues);
	}, [defaultValues]);

	// @ts-ignore
	useEffectAsync(async () => {
		await handleLoadWorkplaceCaseTypes();
		if (patientId) {
			handleSetPatientTimezone();
		}
	}, []);

	useEffect(() => {
		if (selectedPatientId || patientId) {
			handleLoadHealthPlans();
		}
	}, [selectedPatientId, patientId]);

	const validationSchema = yup.object({
		patientId:
			selectedPatientId || order?.event?.extendedProps.patient.id
				? yup.mixed().notRequired()
				: yup.mixed().required(t('validation.calendarPatientEvent.patientId')),
		medicalProfessional: yup
			.object()
			.test(
				'empty-check',
				t('validation.calendarPatientEvent.medicalProfessional') ?? '',
				value => {
					return Boolean(value?.value);
				},
			)
			.required(t('validation.calendarPatientEvent.medicalProfessional')),
		...(isNurseView
			? {}
			: {
					caseType: isCaseTypeSelectVisible
						? yup
								.mixed()
								.required(t('validation.calendarPatientEvent.caseType'))
						: yup.mixed().notRequired(),
			  }),
		specification: !toggleOther
			? yup.mixed().notRequired()
			: yup
					.string()
					.required(t('validation.calendarPatientEvent.specification')),
		meetingType: yup
			.mixed()
			.required(t('validation.calendarPatientEvent.meetingType')),
		date: yup.date().required(t('validation.calendarPatientEvent.date')),
		timeFrom: yup
			.date()
			.nullable()
			.default(undefined)
			.required(t('validation.calendarPatientEvent.timeFrom')),
		timeTo: yup
			.date()
			.nullable()
			.default(undefined)
			.test(
				'is-greater',
				t('validation.calendarPatientEvent.timeDiff') ?? ' ',
				function () {
					const { date, timeFrom, timeTo } = this.parent;
					return checkIsTimeSameOrAfter(date, timeFrom, timeTo);
				},
			)
			.required(t('validation.calendarPatientEvent.timeTo')),
		note: yup.string().nullable().notRequired(),
		healthPlan: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	const handleLoadWorkplaceCaseTypes = async () => {
		setLoadingWorkplaceCaseTypes(true);

		const data = await getWorkplaceCaseTypesDoctor({
			workplaceId: workplaceId,
			caseTypeTypes: [CaseTypeType.CaseType],
			pagination: {
				take: 9999,
			},
		});

		const temp: WorkplaceCaseTypeSelectValue[] = [];

		for (const workplaceCaseType of data?.data?.workplaceCaseTypesDoctor
			?.data || []) {
			if (
				workplaceCaseType?.caseType?.type === 'caseType' &&
				workplaceCaseType.caseType.fullPath
			) {
				temp.push({
					value: workplaceCaseType.id,
					label: workplaceCaseType.caseType.fullPath,
					eventInterval: workplaceCaseType.caseType.eventInterval,
				});
			}
		}

		temp.push({
			value: 'other',
			label: t('base.other'),
		});

		setWorkplaceCaseTypes(temp);
		setLoadingWorkplaceCaseTypes(false);
	};

	const handleLoadHealthPlans = async () => {
		try {
			setAreHealthPlansLoading(true);

			const healthPlans = await getHealthPlans().unwrap();
			const rows = healthPlans?.healthPlans?.data || [];
			const healthPlanUsers = await Promise.all(
				rows.map(row =>
					getHealthPlanUser({
						userId: patientId ?? selectedPatientId,
						healthPlanCode: row?.code,
					}).unwrap(),
				),
			);

			const options: HealthPlanSelectValue[] = healthPlanUsers
				.filter(({ healthPlanUser }) => healthPlanUser?.isActive)
				.map(({ healthPlanUser }) => ({
					label: `${healthPlanUser?.healthPlan?.name}`,
					value: `${healthPlanUser?.healthPlan?.code}`,
					id: healthPlanUser?.id,
				}));

			setHealthPlanOptions(options);
			setAreHealthPlansLoading(false);
		} catch (error: any) {
			showNotificationAlert({
				content: error.response.data.message,
			});
		}
	};

	const handleCreate = async (values: CalendarPatientEventFormValues) => {
		setDisabled(true);

		const {
			date,
			note,
			caseType,
			meetingType,
			medicalProfessional,
			specification,
			healthPlan,
			paidMeeting,
		} = values;
		let { timeFrom, timeTo } = values;
		const tempPatientId = selectedPatientId || values?.patientId?.value;

		const eventMembers = [];

		if (tempPatientId) {
			eventMembers.push({
				userId: tempPatientId,
			});
		}

		if (medicalProfessional?.value) {
			eventMembers.push({
				workplaceMemberId: medicalProfessional?.value,
			});
		}

		const meta = [];

		if (caseType && caseType.value != 'other') {
			meta.push({
				key: 'caseType',
				value: caseType.value,
			});
		}

		if (meetingType) {
			meta.push({
				key: 'meetingType',
				value: meetingType.value,
			});
		}

		if (params?.diabetesInitEvent) {
			meta.push({
				key: 'diabetesInitEvent',
				value: true,
			});
		}

		meta.push({
			key: 'paidMeeting',
			value: paidMeeting,
		});

		const day = moment(date);
		const timeFromMoment = getMomentDate(timeFrom);
		const timeToMoment = getMomentDate(timeTo);

		timeFrom = adjustDstBreakPoint(
			day.clone().set({
				hour: timeFromMoment.hours(),
				minute: timeFromMoment.minutes(),
				second: 0,
			}),
		);

		timeTo = adjustDstBreakPoint(
			day.clone().set({
				hour: timeToMoment.hours(),
				minute: timeToMoment.minutes(),
				second: 0,
			}),
		);

		if (healthPlanCode) {
			meta.push({
				key: 'healthPlanCode',
				value: HealthPlanCode,
			});
			meta.push({
				key: 'healthPlanUserItemKey',
				value: 'appointment1Month1',
			});
		}

		const response = await apiClient.post('/calendar/event/', {
			dateFrom: day
				.clone()
				.startOf('day')
				.add(timeFrom.format('HH'), 'hours')
				.add(timeFrom.format('mm'), 'minutes'), //moment(date).format("yyyy-MM-DD") + 'T' + moment(timeFrom).format('HH:mm:ss'),
			dateTo: day
				.clone()
				.startOf('day')
				.add(timeTo.format('HH'), 'hours')
				.add(timeTo.format('mm'), 'minutes'), // moment(date).format("yyyy-MM-DD") + 'T' + moment(timeTo).format('HH:mm:ss'),
			type: 'default',
			eventMembers,
			propIgnoreFreeTimeCheck: true,
			workplaceId: workplaceId,
			description: note ? note : '',
			caseId: caseId,
			title: specification ? specification : caseType?.label,
			meta,
			healthPlanUserId: healthPlan?.id,
			ignoreMe: true,
			calendarId: calendar?.id,
		});

		if (onEventIsCreated) {
			onEventIsCreated(response);
		}

		if (response.data?.error) {
			setDisabled(false);
		} else {
			showNotificationSuccess({
				content: t('base.shared.patientOrdered'),
			});
			onSuccess?.();
		}
	};

	const handleRemove = async function () {
		await apiClient.delete('/events/cancel/' + order.event.id);
		onSuccess?.();
	};

	const handleUpdate = async (values: CalendarPatientEventFormValues) => {
		setDisabled(true);
		let { timeFrom, timeTo } = values;
		const {
			date,
			patientId,
			note,
			caseType,
			meetingType,
			healthPlan,
			paidMeeting,
		} = values;
		const day = moment(date);
		timeFrom = getMomentDate(timeFrom);
		timeTo = getMomentDate(timeTo);

		const meta = [
			{
				key: 'meetingType',
				value: meetingType.value,
			},
			{
				key: 'paidMeeting',
				value: paidMeeting,
			},
		];

		if (caseType?.value) {
			meta.push({
				key: 'caseType',
				value: caseType?.value,
			});
		}

		const response = await apiClient.patch('/event/' + order.event.id, {
			dateFrom: day
				.clone()
				.startOf('day')
				.add(timeFrom.hours(), 'hours')
				.add(timeFrom.minutes(), 'minutes'), //moment(date).format("yyyy-MM-DD") + 'T' + moment(timeFrom).format('HH:mm:ss'),
			dateTo: day
				.clone()
				.startOf('day')
				.add(timeTo.hours(), 'hours')
				.add(timeTo.minutes(), 'minutes'), // moment(date).format("yyyy-MM-DD") + 'T' + moment(timeTo).format('HH:mm:ss'),
			eventMembers: [
				{
					userId: order?.event?.extendedProps.patient.id ?? patientId,
				},
				{
					workplaceMemberId: order?.event?.extendedProps?.doctor?.id,
				},
			],
			propIgnoreFreeTimeCheck: true,
			description: note ?? '',
			title: caseType?.label,
			workplaceId: sendWorkplaceId ? workplaceId : undefined,
			meta: meta,
			healthPlanUserId: healthPlan?.id,
			ignoreMe: true,
		});

		if (!response?.data?.error) {
			onSuccess?.();
		}
		setDisabled(false);
	};

	useEffect(() => {
		if (!order) {
			return;
		}

		setLoading(true);

		const tempDefaultValues: Record<string, any> = {
			date: order?.event?.start ? getMomentDate(order.event.start) : undefined,
			timeFrom: order?.event?.start
				? getMomentDate(order.event.start)
				: undefined,
			timeTo: order?.event?.end ? getMomentDate(order.event.end) : undefined,
			note: order?.event?.extendedProps?.note,
		};

		if (order) {
			const meta: Meta[] = order?.event?.extendedProps?.meta ?? [];

			if (order?.event?.extendedProps?.meta) {
				const paidMeetingMeta = meta.find(m => m.key === 'paidMeeting');

				if (paidMeetingMeta) {
					tempDefaultValues['paidMeeting'] = paidMeetingMeta.value === true;
				}

				const caseTypeMeta = meta.find(m => m.key === 'caseType');

				if (caseTypeMeta) {
					tempDefaultValues['caseType'] = workplaceCaseTypes.find(
						p => p.value === caseTypeMeta.value,
					);
					setToggleOther(false);
				} else {
					tempDefaultValues['caseType'] = 'other';
					tempDefaultValues['specification'] = order?.event?.title;
					setToggleOther(true);
				}

				const meetingTypeMeta = meta.find(m => m.key === 'meetingType');

				if (meetingTypeMeta) {
					switch (meetingTypeMeta.value) {
						case 'telemedicine':
							tempDefaultValues['meetingType'] = {
								value: 'telemedicine',
								label: t('base.meetingType.telemedicine'),
							};
							break;
						case 'onSite':
							tempDefaultValues['meetingType'] = {
								value: 'onSite',
								label: t('base.meetingType.onSite'),
							};
							break;
					}
				}
			}
		}

		setTimeFrom(tempDefaultValues.timeFrom);
		setTimeTo(tempDefaultValues.timeTo);
		setDefaultValues(tempDefaultValues);

		formMethods.reset(tempDefaultValues);

		setLoading(false);
	}, [order, workplaceCaseTypes]);

	const handleCalculateTimeTo = async (value: WorkplaceCaseTypeSelectValue) => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();

		if (value.value === 'other') {
			setToggleOther(true);
		} else {
			formMethods.setValue('specification', undefined);
			setToggleOther(false);
		}

		if (value.eventInterval && value.eventInterval > 0) {
			formMethods.setValue(
				'timeTo',
				formValues.timeFrom.clone().add(value.eventInterval, 'minutes'),
			);
		}
	};

	const handleSetPatientTimezone = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setSelectedPatientId(patientId ?? formValues?.patientId?.value);
		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	const handleGetPatientsTimes = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();

		setTimeFrom(formValues.timeFrom);
		setTimeTo(formValues.timeTo);
	};

	const handlePatientTimeDiffChange = (change: PatientTimeZoneInfoValues) => {
		setUsersTimeDiff(change.timeDiff);
		setPatientTimezone(change.patientTimezone);
	};

	useEffect(() => {
		if (order?.event?.extendedProps?.doctor?.id) {
			formMethods.setValue('medicalProfessional', {
				value: order.event.extendedProps.doctor.id,
				label: order?.event?.extendedProps?.doctor?.name,
			});
		} else {
			if (isDoctor) {
				formMethods.setValue('medicalProfessional', { value: userId });
			}
		}
	}, [userId, isDoctor]);

	const healthPlanValue = formMethods.watch('healthPlan')?.value;
	useEffect(() => {
		if (healthPlanValue && healthPlanValue != 'withoutHealthPlan') {
			setIsCaseTypeSelectVisible(false);
		} else {
			setIsCaseTypeSelectVisible(true);
		}
	}, [healthPlanValue]);

	useEffect(() => {
		const eventsWithInvisibleCaseType: string[] = [
			'meddibaby_appointment',
			'meddibaby_first_appointment',
			'diabetes_appointment',
			'diabetes_first_appointment',
		];
		for (const invisibleItemKey of eventsWithInvisibleCaseType) {
			if (
				order?.event?.extendedProps?.meta?.some(
					(item: any) => item.key === invisibleItemKey,
				)
			) {
				setIsCaseTypeSelectVisible(false);
			}
		}
	}, [order]);

	const handleRegisterNewPatient = (
		params: PatientManualRegistrationParams,
	) => {
		setSelectedPatientId(params.patientId);
		setIsRegisterNewPatientOpen(false);
	};

	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<FormWrapper formMethods={formMethods}>
				{selectedPatientId || order?.event?.extendedProps.patient.id ? (
					<div className={'mb-4'}>
						<PatientMiniProfile
							userId={
								selectedPatientId ?? order?.event?.extendedProps?.patient?.id
							}
							workplaceId={workplaceId}
							loggedUserId={loggedUserId}
							showVerifyInformation={false}
							links={null}
							showAge={true}
							showBirthDate={false}
							showAddress={false}
							showPhone={false}
							showEmail={false}
							showInsuranceCompany={false}
							showGender={false}
						/>
					</div>
				) : (
					<div className={'mb-3'}>
						<FormControl
							label='base.shared.searchPatient'
							name='patientId'
							onChange={handleSetPatientTimezone}
							isFormContextDisabled
							isRequired={true}>
							<PatientSelect workplaceId={workplaceId} />
						</FormControl>
						<LinkButton
							theme={Theme.BLUE}
							onClick={() => setIsRegisterNewPatientOpen(true)}
							className='btn-link blue text-underline'
							title='component.searchingPatients.patientNotRegistered'
						/>
						<hr />
					</div>
				)}
				<div>
					<div style={{ marginBottom: '10px' }}>
						<DoctorSelectField
							isRequired
							name='medicalProfessional'
							label='base.professionalName'
							roleKeys={['doctor', 'therapist', 'nurse']}
							approved
						/>
					</div>
					{selectedPatientId &&
						!areHealthPlansLoading &&
						healthPlanOptions.length > 0 && (
							<FormControl name='healthPlan'>
								<Select
									label='component.appointment.pickHealthPlan'
									placeholder={t('base.shared.pick')}
									options={[
										...[
											{
												label: t('base.withoutHealthPlan'),
												value: 'withoutHealthPlan',
											},
										],
										...healthPlanOptions,
									]}
								/>
							</FormControl>
						)}
					{loadingWorkplaceCaseTypes && <Loading />}
					{!loadingWorkplaceCaseTypes && isCaseTypeSelectVisible && (
						<FormControl
							label='base.pickCaseType'
							name='caseType'
							isFormContextDisabled
							onChange={handleCalculateTimeTo}
							isRequired={true}>
							<Select
								placeholder={t('base.shared.pick')}
								options={workplaceCaseTypes}
							/>
						</FormControl>
					)}
					{toggleOther && (
						<FormControl
							label='base.specification'
							isFormContextDisabled
							name='specification'
							isRequired={true}>
							<TextField />
						</FormControl>
					)}
					<FormControl
						label='base.typeOfEvent'
						name='meetingType'
						isFormContextDisabled
						isRequired={true}>
						<Select
							placeholder={t('base.shared.pick')}
							options={[
								{
									value: 'telemedicine',
									label: t('base.meetingType.telemedicine'),
								},
								{
									value: 'onSite',
									label: t('base.meetingType.onSite'),
								},
							]}
						/>
					</FormControl>
					{!!usersTimeDiff && (
						<div className={styles.warning}>
							<Text tx={'base.patientsIsInTimeZone.part1'} inline firstUpper />{' '}
							({patientTimezone}){', '}
							<span className={styles.warningBold}>{usersTimeDiff}</span>{' '}
							<Text
								tx={'base.shared.hourMiddle'}
								className={styles.warningBold}
								inline
							/>{' '}
							<Text tx={'base.patientsIsInTimeZone.part2'} inline />.
						</div>
					)}
					<div className={'row'}>
						<div className={'col-4'}>
							<FormControl
								label='base.shared.date'
								name='date'
								isFormContextDisabled
								isRequired={true}>
								<DatePicker minDate={moment()} />
							</FormControl>
						</div>
						<div className={'col-4'}>
							<FormControl
								label='base.shared.from'
								name='timeFrom'
								isFormContextDisabled
								onChange={handleGetPatientsTimes}
								isRequired={true}>
								<TimePicker />
							</FormControl>
							{selectedPatientId && (
								<PatientTimeZoneInfo
									patientId={selectedPatientId}
									time={timeFrom}
									onChange={handlePatientTimeDiffChange}
								/>
							)}
						</div>
						<div className={'col-4'}>
							<FormControl
								label='base.shared.to'
								name='timeTo'
								isFormContextDisabled
								onChange={handleGetPatientsTimes}
								isRequired={true}>
								<TimePicker />
							</FormControl>
							{selectedPatientId && (
								<PatientTimeZoneInfo
									patientId={selectedPatientId}
									time={timeTo}
								/>
							)}
						</div>
					</div>
					<FormControl
						label='base.shared.noteVisibleForPatient'
						name='note'
						isFormContextDisabled>
						<TextArea />
					</FormControl>
					{isLatam && (
						<FormControl name='paidMeeting'>
							<Checkbox tx='component.calendarPatientEvent.paidMeeting' />
						</FormControl>
					)}
					<div
						className={'d-flex flex-row align-items-center mt-4'}
						style={{ gap: '15px' }}>
						{!order ? (
							<SubmitButton
								onSubmit={handleCreate}
								title='component.eventDetail.saveToCalendarAndSendInfoToPatient'
								disabled={disabled}
							/>
						) : (
							<>
								<SubmitButton
									onSubmit={handleUpdate}
									title={'component.calendar.context.edit'}
									disabled={disabled}
								/>
								<div className={'ml-3'}>
									<RemoveButton onClick={handleRemove} />
								</div>
							</>
						)}
					</div>
				</div>
			</FormWrapper>
			{isRegisterNewPatientOpen && (
				<PatientManualRegistrationModal
					workplaceId={workplaceId}
					onClose={() => setIsRegisterNewPatientOpen(false)}
					onSuccess={handleRegisterNewPatient}
					isOpened
				/>
			)}
		</>
	);
};

export default CalendarPatientEvent;
